import { useContext, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import ButtonComponent from 'components/design-system/Button/Button';
import InputComponent from 'components/design-system/input/Input';
import LabelComponent from 'components/design-system/Label/Label';
import SelectComponent from 'components/design-system/Autocomplete/Autocomplete';
import { MatchContext } from '../../../context/MatchContext';
import { ManageSearchOutlined } from '@mui/icons-material';
import Text from 'components/design-system/Text/Text';
import { DatePickerComponent } from 'components/design-system/DatePicker/DatePicker';
import { PERMISOS } from 'const/Permisos';
import { VerificarPermisos } from 'components/VerificarPermisos/VerificarPermisos';
import { AuthContext } from 'context';

/**
 * @name NuevosCriteriosCampos
 * Componentes con campos de formulario para nuevos criterios de match manual.
 */
export const NuevosCriteriosCampos = ({
  handleChange,
  values,
  touched,
  errors,
  setFieldTouched,
  isSubmitting,
  validateForm,
  setFieldValue,
  setIsNuevosCriterios
}) => {
  const { isTienePermisos } = useContext(AuthContext);

  const {
    listaTipoResiduos,
    listaTipoRecursos,
    listaEmpresas,
    listaSucursales,
    getSucursal,
    tieneNegocio,
    isCargandoListaSucursales,
    isCargandoResiduosRecursos,
    DISABLE_TAB_RESIDUOS,
    handleCargarResiduosYRecursos,
    handleCargarRecursos,
    handleReCargarRecursos
  } = useContext(MatchContext);

  console.log('touched', touched);

  useEffect(() => {
    if (!isTienePermisos(PERMISOS.matchFiltroNegocio)) return;
    if (!values.empresa) return;
    getSucursal(values.empresa.codigoEmpresa);
  }, [values.empresa]);

  useEffect(() => {
    if (!isTienePermisos(PERMISOS.matchFiltroSucursal)) return;
    setFieldValue('sucursales', listaSucursales);
  }, [setFieldValue, listaSucursales]);

  useEffect(() => {
    if (
      values.sucursal &&
      values.sucursal.sucursalesTipoResiduo &&
      touched.sucursal
    ) {
      handleCargarResiduosYRecursos(values.sucursal.sucursalesTipoResiduo);
    }
  }, [values.sucursal]);

  useEffect(() => {
    if (values.codigosTipoResiduo) {
      handleCargarRecursos(values.codigosTipoResiduo.codigoTipoResiduo);
    }
  }, [values.codigosTipoResiduo]);

  return (
    <div className="col-span-12">
      <div className="grid grid-cols-12 gap-5">
        <VerificarPermisos nombrePermiso={PERMISOS.matchFiltroNegocio}>
          <div className="col-span-12 sm:col-span-6 lg:col-span-6">
            <SelectComponent
              name="empresa"
              options={listaEmpresas}
              accesor="nombreEmpresa"
              value={values.empresa}
              getOptionLabel={option => (option ? option.nombreEmpresa : '')}
              clearOnEscape={true}
              openOnFocus={true}
              noOptionsText="Sin opciones"
              onBlur={() => setFieldTouched('empresa')}
              touched={touched.empresa ? true : false}
              onChange={(ev, tipoResiduo, reason) => {
                let data = {
                  target: { name: 'empresa', value: tipoResiduo }
                };
                if (reason === 'clear') {
                  data.target.value = '';
                  setFieldValue('sucursales', []);
                  setFieldValue('codigosTipoResiduo', null);
                  setFieldValue('codigosRecurso', null);
                }
                setFieldValue('sucursal', null);
                handleChange(data);
              }}
            >
              <InputComponent
                fullWidth
                className="h-9"
                touched={touched.empresa ? true : false}
                estado={
                  errors.empresa
                    ? { tipo: 'error', mensaje: errors.empresa }
                    : null
                }
                label={
                  <LabelComponent className="block font-secondary text-xs text-black tracking-wider font-medium pb-1">
                    NEGOCIO
                  </LabelComponent>
                }
              />
            </SelectComponent>
          </div>
        </VerificarPermisos>

        <VerificarPermisos nombrePermiso={PERMISOS.matchFiltroSucursal}>
          <div className="col-span-12 sm:col-span-6 lg:col-span-6">
            <SelectComponent
              name="sucursal"
              disabled={isCargandoListaSucursales}
              isLoading={isCargandoListaSucursales}
              options={values.sucursales}
              value={values.sucursal}
              accesor="nombreSucursal"
              getOptionLabel={option => (option ? option.nombreSucursal : '')}
              clearOnEscape={true}
              openOnFocus={true}
              noOptionsText="Sin opciones"
              onBlur={() => setFieldTouched('sucursal')}
              touched={touched.sucursal ? true : false}
              onChange={(ev, recursos, reason) => {
                let data = {
                  target: { name: 'sucursal', value: recursos }
                };

                if (reason === 'clear') {
                  data.target.value = '';
                }
                setFieldValue('codigosTipoResiduo', null);
                setFieldValue('codigosRecurso', null);
                handleChange(data);
              }}
            >
              <InputComponent
                fullWidth
                className="h-9"
                touched={touched.sucursal ? true : false}
                estado={
                  errors.sucursal
                    ? { tipo: 'error', mensaje: errors.sucursal }
                    : null
                }
                label={
                  <LabelComponent className="block font-secondary text-xs text-black tracking-wider font-medium pb-1">
                    SUCURSAL
                  </LabelComponent>
                }
              />
            </SelectComponent>
          </div>
        </VerificarPermisos>

        <div className="col-span-12 sm:col-span-6 lg:col-span-6">
          <SelectComponent
            name="codigosTipoResiduo"
            disabled={isCargandoResiduosRecursos || isSubmitting}
            isLoading={isCargandoResiduosRecursos}
            options={listaTipoResiduos}
            accesor="nombreTipo"
            value={values.codigosTipoResiduo}
            getOptionLabel={option => (option ? option.nombreTipo : '')}
            clearOnEscape={true}
            openOnFocus={true}
            noOptionsText="Sin opciones"
            onBlur={() => setFieldTouched('codigosTipoResiduo')}
            touched={touched.codigosTipoResiduo ? true : false}
            onChange={(ev, tipoResiduo, reason) => {
              let data = {
                target: { name: 'codigosTipoResiduo', value: tipoResiduo }
              };
              if (reason === 'clear') {
                data.target.value = '';
                setFieldValue('codigosRecurso', null);
                handleReCargarRecursos();
              }
              handleChange(data);
            }}
          >
            <InputComponent
              fullWidth
              className="h-9"
              touched={touched.codigosTipoResiduo ? true : false}
              estado={
                errors.codigosTipoResiduo
                  ? { tipo: 'error', mensaje: errors.codigosTipoResiduo }
                  : null
              }
              label={
                <LabelComponent className="block font-secondary text-xs text-black tracking-wider font-medium pb-1">
                  RESIDUOS
                </LabelComponent>
              }
            />
          </SelectComponent>
        </div>
        <div className="col-span-12 sm:col-span-6 lg:col-span-6">
          <SelectComponent
            name="codigosRecurso"
            disabled={isCargandoResiduosRecursos || isSubmitting}
            isLoading={isCargandoResiduosRecursos}
            options={listaTipoRecursos}
            value={values.codigosRecurso}
            accesor="nombreRecurso"
            getOptionLabel={option => (option ? option.nombreRecurso : '')}
            clearOnEscape={true}
            openOnFocus={true}
            noOptionsText="Sin opciones"
            onBlur={() => setFieldTouched('codigosRecurso')}
            touched={touched.codigosRecurso ? true : false}
            onChange={(ev, recursos, reason) => {
              let data = {
                target: { name: 'codigosRecurso', value: recursos }
              };

              if (reason === 'clear') {
                data.target.value = '';
              }
              handleChange(data);
            }}
          >
            <InputComponent
              fullWidth
              className="h-9"
              touched={touched.codigosRecurso ? true : false}
              estado={
                errors.codigosRecurso
                  ? { tipo: 'error', mensaje: errors.codigosRecurso }
                  : null
              }
              label={
                <LabelComponent className="block font-secondary text-xs text-black tracking-wider font-medium pb-1">
                  RECURSOS
                </LabelComponent>
              }
            />
          </SelectComponent>
        </div>

        {!DISABLE_TAB_RESIDUOS && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <div className="col-span-12 sm:col-span-12 lg:col-span-6">
              <LabelComponent>Disponibilidad del Residuo</LabelComponent>
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 sm:col-span-6">
                  <DesktopDatePicker
                    disabled={!tieneNegocio}
                    name="fechaDisponibilidadInicio"
                    inputFormat="dd-MM-yyyy"
                    value={values.fechaDisponibilidadInicio}
                    defaultValue={values.fechaDisponibilidadInicio}
                    maxDate={values.fechaDisponibilidadFin}
                    onChange={value => {
                      handleChange({
                        target: {
                          name: 'fechaDisponibilidadInicio',
                          value: value
                        }
                      });
                    }}
                    slots={{ textField: DatePickerComponent }}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <DesktopDatePicker
                    disabled={!tieneNegocio}
                    name="fechaDisponibilidadFin"
                    inputFormat="dd-MM-yyyy"
                    value={values.fechaDisponibilidadFin}
                    defaultValue={values.fechaDisponibilidadFin}
                    onChange={value => {
                      handleChange({
                        target: {
                          name: 'fechaDisponibilidadFin',
                          value: value
                        }
                      });
                    }}
                    minDate={values.fechaDisponibilidadInicio}
                    slots={{ textField: DatePickerComponent }}
                  />
                </div>
              </div>

              {tieneNegocio ? (
                <Text size="S" className="pt-2">
                  Este criterio solo aplica a{' '}
                  <strong className="text-black font-semibold">
                    búsqueda de residuos
                  </strong>
                </Text>
              ) : (
                <div className="bg-warning-light rounded px-4 py-2 mt-2">
                  <Text size="S">
                    Opciones de búsqueda adicionales para usuarios que hayan{' '}
                    <b className="text-black">registrado su negocio</b>
                  </Text>
                </div>
              )}
            </div>
          </LocalizationProvider>
        )}

        <div className="col-span-12 lg:col-span-6">
          <div
            className={`
            grid grid-cols-12 gap-4 items-end content-end
            ${DISABLE_TAB_RESIDUOS ? '' : 'lg:mt-7'}
          `}
          >
            <div className="col-span-6 lg:col-span-6">
              <ButtonComponent
                form="nuevosCriterios"
                isLoading={isSubmitting}
                disabled={
                  Object.keys(errors).length === 0 && !isSubmitting
                    ? false
                    : true
                }
                buttonType="submit"
                onClick={() => {
                  validateForm()
                    .then(result => {})
                    .catch(err => {
                      console.log(err);
                    });
                }}
                className="px-3 w-full"
              >
                {isSubmitting ? (
                  'Cargando'
                ) : (
                  <>
                    <ManageSearchOutlined className="mr-1" /> Buscar
                  </>
                )}
              </ButtonComponent>
            </div>
            <div className="col-span-6 lg:col-span-6">
              {!isSubmitting && (
                <ButtonComponent
                  type="secondary"
                  fullWidth
                  className="justify-center"
                  onClick={() => setIsNuevosCriterios(false)}
                >
                  Cancelar
                </ButtonComponent>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
